import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { LinkButton } from '../components/ui/link-button';
import {
  Shield,
  Users,
  Dumbbell,
  Coins,
  Sparkles,
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

// Admin Components
import { UsersTab, RewardsTab, ExercisesTab, AdminStatsCards } from '../components/admin';
import { WeeklyTemplatesManager } from '../components/WeeklyTemplatesManager';
import { FeedbacksManager } from '../components/FeedbacksManager';
import { SuperSeriesManager } from '../components/SuperSeriesManager';
import { BalanceManager } from '../components/BalanceManager';
import { XPRulesManager } from '../components/XPRulesManager';
import { StudentsStatusDashboard } from '../components/StudentsStatusDashboard';
import { SavedWorkoutsManager } from '../components/SavedWorkoutsManager';
import SettingsTab from '../components/admin/SettingsTab';
import AiWorkoutTab from '../components/admin/ai/AiWorkoutTab';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminPanel = ({ user }) => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [rewards, setRewards] = useState([]);
  const [stats, setStats] = useState(null);
  const [exerciseLibrary, setExerciseLibrary] = useState([]);
  const [savedWorkouts, setSavedWorkouts] = useState([]);
  const [savedWorkoutCategories, setSavedWorkoutCategories] = useState([]);
  const [superSeries, setSuperSeries] = useState([]);
  const [feedbacks, setFeedbacks] = useState([]);
  const [directMessages, setDirectMessages] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  const ADMIN_CACHE_KEY = 'ferraz_admin_cache';

  const saveAdminCache = (data) => {
    try { localStorage.setItem(ADMIN_CACHE_KEY, JSON.stringify({ ...data, cachedAt: Date.now() })); } catch (e) { console.warn('[AdminPanel] non-critical failure', e); }
  };

  const loadAdminCache = () => {
    try {
      const raw = localStorage.getItem(ADMIN_CACHE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  };

  const applyData = (data) => {
    if (data.users) setUsers(data.users);
    if (data.rewards) setRewards(data.rewards);
    if (data.stats) setStats(data.stats);
    if (data.exerciseLibrary) setExerciseLibrary(data.exerciseLibrary);
    if (data.savedWorkouts) setSavedWorkouts(data.savedWorkouts);
    if (data.savedWorkoutCategories) setSavedWorkoutCategories(data.savedWorkoutCategories);
    if (data.superSeries) setSuperSeries(data.superSeries);
    if (data.feedbacks) setFeedbacks(data.feedbacks);
    if (data.directMessages) setDirectMessages(data.directMessages);
  };

  const fetchAdminData = useCallback(async () => {
    // Only critical data needed for the default Dashboard tab (header stats + users list for search)
    try {
      const [usersRes, statsRes] = await Promise.allSettled([
        axios.get(`${API}/api/admin/users`),
        axios.get(`${API}/api/admin/stats`),
      ]);
      const fresh = {};
      let hasError = false;
      if (usersRes.status === 'fulfilled') { setUsers(usersRes.value.data); fresh.users = usersRes.value.data; }
      else hasError = true;
      if (statsRes.status === 'fulfilled') { setStats(statsRes.value.data); fresh.stats = statsRes.value.data; }
      else hasError = true;
      if (Object.keys(fresh).length > 0) saveAdminCache(fresh);
      if (hasError) setTimeout(() => fetchAdminData(), 5000);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      setTimeout(() => fetchAdminData(), 5000);
    }
  }, []);

  // Lazy loaders per tab, only fetch first time a tab is opened
  const loadedTabsRef = useRef(new Set());
  const [activeTab, setActiveTab] = useState('dashboard');

  const fetchTab = useCallback(async (tab) => {
    if (loadedTabsRef.current.has(tab)) return;
    loadedTabsRef.current.add(tab);
    try {
      if (tab === 'super-series') {
        const r = await axios.get(`${API}/api/admin/super-series`).catch(() => null);
        if (r) setSuperSeries(r.data);
      } else if (tab === 'workouts' || tab === 'templates') {
        // Both tabs use savedWorkouts + exerciseLibrary + superSeries
        const [sw, swc, ex, ss] = await Promise.allSettled([
          axios.get(`${API}/api/admin/saved-workouts`),
          axios.get(`${API}/api/admin/saved-workouts/categories/list`),
          axios.get(`${API}/api/admin/exercises`),
          axios.get(`${API}/api/admin/super-series`),
        ]);
        if (sw.status === 'fulfilled') setSavedWorkouts(sw.value.data);
        if (swc.status === 'fulfilled') setSavedWorkoutCategories(swc.value.data);
        if (ex.status === 'fulfilled') setExerciseLibrary(ex.value.data);
        if (ss.status === 'fulfilled') setSuperSeries(ss.value.data);
        loadedTabsRef.current.add('super-series');
      } else if (tab === 'feedbacks') {
        const [fb, msg] = await Promise.allSettled([
          axios.get(`${API}/api/admin/feedbacks`),
          axios.get(`${API}/api/admin/messages`),
        ]);
        if (fb.status === 'fulfilled') setFeedbacks(fb.value.data);
        if (msg.status === 'fulfilled') setDirectMessages(msg.value.data);
      } else if (tab === 'rewards') {
        const r = await axios.get(`${API}/api/admin/rewards`).catch(() => null);
        if (r) setRewards(r.data);
      }
    } catch (error) {
      console.error(`Erro ao carregar aba ${tab}:`, error);
      loadedTabsRef.current.delete(tab);
    }
  }, []);

  useEffect(() => {
    if (user?.role === 'admin') {
      // Load cache first for instant display
      const cached = loadAdminCache();
      if (cached) applyData(cached);
      // Then fetch fresh data
      fetchAdminData();
    }
  }, [user, fetchAdminData]);

  if (user?.role !== 'admin') {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Shield className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">
            Acesso Negado
          </h2>
          <p className="text-zinc-400">
            Você não tem permissão para acessar esta área.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 lg:p-12 space-y-8" data-testid="admin-panel">
      <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
        <div className="flex items-center gap-3 mb-2">
          <Shield className="w-8 h-8 text-neon-lime" />
          <h1 className="font-primary font-black text-5xl text-white uppercase tracking-tighter">
            Painel Admin
          </h1>
        </div>
        <p className="text-zinc-400 font-secondary text-lg">
          Gerencie participantes e configurações
        </p>
      </motion.div>

      <AdminStatsCards stats={stats} />

      <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); fetchTab(v); }} className="space-y-6">
        <TabsList className="bg-zinc-900/50 border border-white/5 flex-wrap h-auto justify-start gap-1 p-1">
          <TabsTrigger value="dashboard" data-testid="dashboard-tab">
            Dashboard
          </TabsTrigger>
          <TabsTrigger value="users" data-testid="users-tab">
            Usuários
          </TabsTrigger>
          <TabsTrigger value="workouts" data-testid="workouts-tab">
            Treinos
          </TabsTrigger>
          <TabsTrigger value="templates" data-testid="templates-tab">
            Referência
          </TabsTrigger>
          <TabsTrigger value="exercises" data-testid="exercises-tab">
            Exercícios
          </TabsTrigger>
          <TabsTrigger value="super-series" data-testid="super-series-tab">
            Super Série
          </TabsTrigger>
          <TabsTrigger value="feedbacks" data-testid="feedbacks-tab">
            Feedbacks
          </TabsTrigger>
          <TabsTrigger value="rewards" data-testid="rewards-tab">
            Recompensas
          </TabsTrigger>
          <TabsTrigger value="balance" data-testid="balance-tab">
            <Coins className="w-4 h-4 mr-1" /> Saldos
          </TabsTrigger>
          <TabsTrigger value="xp-rules" data-testid="xp-rules-tab">
            <Sparkles className="w-4 h-4 mr-1" /> Regras XP
          </TabsTrigger>
          <TabsTrigger value="ai-workouts" data-testid="ai-workouts-tab">
            <Sparkles className="w-4 h-4 mr-1" /> Treino IA
          </TabsTrigger>
          <TabsTrigger value="settings" data-testid="settings-tab">
            Configurações
          </TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard" className="space-y-4">
          <StudentsStatusDashboard user={user} />
        </TabsContent>

        <TabsContent value="super-series" className="space-y-4">
          <SuperSeriesManager />
        </TabsContent>

        <TabsContent value="exercises" className="space-y-4">
          <ExercisesTab />
        </TabsContent>

        <TabsContent value="workouts" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Treinos por Aluno */}
            <div className="bg-zinc-900/50 border border-neon-lime/30 rounded-2xl p-6">
              <Users className="w-12 h-12 text-neon-lime mb-4" />
              <h3 className="font-primary font-bold text-xl text-white mb-2">
                Treinos por Aluno
              </h3>
              <p className="text-zinc-400 text-sm mb-4">
                Gerencie treinos individualizados para cada aluno cadastrado
              </p>
              <LinkButton
                to="/admin/users-workouts"
                variant="full"
                data-testid="admin-manage-students-btn"
              >
                <Users className="w-5 h-5 mr-2 inline" />
                Gerenciar por Aluno
              </LinkButton>
            </div>

            {/* Treinos Gerais (Legacy) */}
            <div className="bg-zinc-900/50 border border-white/5 rounded-2xl p-6">
              <Dumbbell className="w-12 h-12 text-zinc-500 mb-4" />
              <h3 className="font-primary font-bold text-xl text-white mb-2">
                Treinos Gerais
              </h3>
              <p className="text-zinc-400 text-sm mb-4">
                Treinos que podem ser atribuídos a múltiplos alunos
              </p>
              <LinkButton
                to="/admin/workouts"
                variant="secondary"
                data-testid="admin-general-workouts-btn"
              >
                <Dumbbell className="w-5 h-5 mr-2 inline" />
                Treinos Gerais
              </LinkButton>
            </div>
          </div>

          {/* Treinos Salvos Section - Using Component */}
          <SavedWorkoutsManager 
            savedWorkouts={savedWorkouts}
            savedWorkoutCategories={savedWorkoutCategories}
            exerciseLibrary={exerciseLibrary}
            superSeries={superSeries}
            onRefresh={fetchAdminData}
          />
        </TabsContent>

        <TabsContent value="templates" className="space-y-4">
          <WeeklyTemplatesManager
            savedWorkouts={savedWorkouts}
            users={users}
            onRefresh={fetchAdminData}
          />
        </TabsContent>

        <TabsContent value="feedbacks" className="space-y-4">
          <FeedbacksManager feedbacks={feedbacks} directMessages={directMessages} allUsers={users} onRefresh={fetchAdminData} />
        </TabsContent>

        <TabsContent value="users" className="space-y-4">
          <UsersTab 
            users={users}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            onRefresh={fetchAdminData}
          />
        </TabsContent>

        <TabsContent value="rewards" className="space-y-4">
          <RewardsTab rewards={rewards} onRefresh={fetchAdminData} />
        </TabsContent>

        <TabsContent value="balance" className="space-y-4">
          <BalanceManager users={users} onRefresh={fetchAdminData} />
        </TabsContent>

        <TabsContent value="xp-rules" className="space-y-4">
          <XPRulesManager />
        </TabsContent>

        <TabsContent value="ai-workouts" className="space-y-4">
          <AiWorkoutTab />
        </TabsContent>

        <TabsContent value="settings" className="space-y-4">
          <SettingsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminPanel;
